import { useEffect, useRef, useState } from "react";
import type { RunState } from "../types/api";
import { fetchRun } from "../api/client";
import {
  displayDash,
  formatDurationMs,
  humanizeSpecialty,
  shortRunId,
} from "../utils/format";
import { averageRcs, evidenceLevelCounts, papersToCsv } from "../utils/papers";
import { ActivityLog } from "./ActivityLog";
import { AnswerSection } from "./AnswerSection";
import { ConsensusCards } from "./ConsensusCards";
import { EvidenceTable } from "./EvidenceTable";
import { StructuredQuery } from "./StructuredQuery";

interface ResultsViewProps {
  state: RunState;
  hasLiveLog: boolean;
  onShowLiveLog: () => void;
  onFollowUp: (q: string) => void;
  onNewSearch: () => void;
  toast: (msg: string) => void;
}

const SECTIONS = [
  { id: "section-answer", num: 1, label: "Synthesized answer" },
  { id: "section-consensus", num: 2, label: "Consensus" },
  { id: "section-evidence", num: 3, label: "Evidence" },
];

export function ResultsView({
  state,
  hasLiveLog,
  onShowLiveLog,
  onFollowUp,
  onNewSearch,
  toast,
}: ResultsViewProps) {
  const [activeSection, setActiveSection] = useState(SECTIONS[0].id);
  const mainRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActiveSection(e.target.id);
        }
      },
      { root, rootMargin: "-20% 0px -60% 0px", threshold: 0 }
    );
    for (const s of SECTIONS) {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const copyAnswer = async () => {
    await navigator.clipboard.writeText(state.supervisorAnswer);
    toast("Answer copied");
  };

  const downloadCsv = () => {
    const blob = new Blob([papersToCsv(state.papers)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `evidence-${shortRunId(state.runId)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const downloadJson = async () => {
    if (!state.runId) return;
    try {
      const run = await fetchRun(state.runId);
      const blob = new Blob([JSON.stringify(run, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `run-${shortRunId(state.runId)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast("Could not download run JSON");
    }
  };

  const levelCounts = evidenceLevelCounts(state.papers);
  const avgRcs = averageRcs(state.papers);
  const manifest = state.manifest;

  return (
    <div className="results-layout">
      <aside className="results-sidebar">
        <nav className="section-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`nav-item${activeSection === s.id ? " active" : ""}`}
              onClick={() => scrollTo(s.id)}
            >
              <span className="nav-num">{s.num}</span> {s.label}
            </button>
          ))}
        </nav>
        <div className="sidebar-stats">
          <p className="sidebar-label">Run stats</p>
          <dl>
            <dt>Duration</dt>
            <dd>{formatDurationMs(state.durationMs)}</dd>
            <dt>Papers</dt>
            <dd>{state.paperCount}</dd>
            <dt>Specialists</dt>
            <dd>
              {state.specialistsUsed.length
                ? state.specialistsUsed.map(humanizeSpecialty).join(", ")
                : "—"}
            </dd>
            <dt>Department</dt>
            <dd>{manifest?.department_label ?? state.departmentLabel ?? "—"}</dd>
            <dt>Index</dt>
            <dd title={manifest?.pinecone_index ?? ""}>
              {(manifest?.pinecone_index ?? "—").slice(0, 24)}
            </dd>
            <dt>Vector store</dt>
            <dd>{manifest?.vector_backend ?? state.vectorStore}</dd>
            <dt>Model</dt>
            <dd title={manifest?.model_id ?? state.modelId ?? ""}>
              {manifest?.model_provider
                ? `${manifest.model_provider} · `
                : ""}
              {(manifest?.model_id ?? state.modelId ?? "—").slice(0, 28)}
            </dd>
            <dt>Framework</dt>
            <dd>{(manifest?.framework ?? state.framework ?? "—").toUpperCase()}</dd>
          </dl>
        </div>
        <div className="sidebar-quality">
          <p className="sidebar-label">Evidence quality</p>
          {Object.entries(levelCounts).map(([lvl, n]) => (
            <div key={lvl} className="quality-row">
              <span>{lvl}</span>
              <span>{n}</span>
            </div>
          ))}
          {avgRcs != null ? (
            <p className="quality-avg">Avg RCS: {avgRcs.toFixed(1)}</p>
          ) : null}
          {state.cci?.carbon_kg_per_month != null ? (
            <p className="quality-cci">
              ~{displayDash(state.cci.carbon_kg_per_month)} kgCO₂e/mo at this rate
            </p>
          ) : null}
        </div>
      </aside>

      <div className="results-main" ref={mainRef}>
        <div className="results-header">
          <div>
            <h1 className="results-query">{state.prompt}</h1>
            {state.runId ? (
              <p className="results-run-id">run {shortRunId(state.runId)}</p>
            ) : null}
          </div>
          <div className="results-actions">
            {hasLiveLog ? (
              <button type="button" className="link-btn" onClick={onShowLiveLog}>
                View live log
              </button>
            ) : null}
            <button type="button" className="btn-secondary" onClick={copyAnswer}>
              Copy answer
            </button>
            <div className="export-group">
              <button type="button" className="btn-primary" onClick={downloadJson}>
                Export
              </button>
              <button type="button" className="btn-secondary btn-sm" onClick={downloadCsv}>
                CSV
              </button>
            </div>
          </div>
        </div>

        {state.warningMessage ? (
          <div className="notice-card warning-banner">{state.warningMessage}</div>
        ) : null}

        <div className="hero-card">
          <StructuredQuery
            framework={state.framework}
            picot={state.picot}
            spice={state.spice}
            rewrittenQuery={state.rewrittenQuery}
          />
        </div>

        <section id="section-answer" className="result-section">
          <h2>1 · Synthesized answer</h2>
          <AnswerSection answer={state.supervisorAnswer} />
        </section>

        <section id="section-consensus" className="result-section">
          <h2>2 · Consensus</h2>
          <ConsensusCards consensus={state.consensus} />
        </section>

        <section id="section-evidence" className="result-section">
          <h2>3 · Evidence</h2>
          <EvidenceTable papers={state.papers} />
          {!state.papers.length ? (
            <button type="button" className="btn-primary" onClick={onNewSearch}>
              New search
            </button>
          ) : null}
        </section>

        {state.followups.length > 0 ? (
          <div className="followups">
            <p className="section-label">Follow-up questions</p>
            <div className="chip-row">
              {state.followups.map((q) => (
                <button key={q} type="button" className="chip" onClick={() => onFollowUp(q)}>
                  {q}
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function LiveLogOverlay({
  state,
  onClose,
}: {
  state: RunState;
  onClose: () => void;
}) {
  return (
    <div className="modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="modal-panel log-overlay"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Live activity log"
      >
        <div className="modal-hdr">
          <h2>Activity log</h2>
          <button type="button" className="modal-close" onClick={onClose}>
            Close
          </button>
        </div>
        <ActivityLog rows={state.log} eventCount={state.logEventCount} />
      </div>
    </div>
  );
}
